import React, { useMemo, useState, useContext } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { FiClock, FiTrendingUp, FiEdit3 } from 'react-icons/fi'
import TradeViewButtonContainer from '../trade-view-button-container/trade-view-button-container'
import TradeContext from '../../../../../store/tradeState/tradeContext';
import AuthContext from '../../../../../auth/auth-context';
import axios from 'axios';
import Button from 'react-bootstrap/Button';

const Container = styled.div`
  max-width: 760px;
`

const Title = styled.h2`
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 800;
  font-size: 1.25rem;
  line-height: 1.2;
  letter-spacing: -0.2px;
  color: #212529;
  position: relative;
  padding-bottom: 8px;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 56px;
    height: 3px;
    border-radius: 999px;
    background: linear-gradient(90deg, #0d6efd 0%, #6f42c1 100%);
  }

  svg {
    color: #0d6efd;
    flex-shrink: 0;
  }
`

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
`

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`

const PairBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 0.95rem;
  color: #212529;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  padding: 6px 10px;
  border-radius: 999px;
`

const Meta = styled.span`
  color: #6c757d;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  white-space: nowrap;
`

const Strong = styled.strong`
  color: #212529;
`

const Body = styled.div`
  border-top: 1px solid #e9ecef;
  padding: 12px 16px 14px;
  background: #ffffff;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 8px 16px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f5;

  &:last-child { border-bottom: none; }
`

const Label = styled.span`
  color: #6c757d;
`

const Value = styled.span`
  color: #212529;
  font-weight: 600;
`

const BookContainer = styled.div`
  width: 100%;
  margin-top: 16px;
`

function TradeViewManualContainer(props) {
  const t = props?.trade || {}

  const tradeCtx = useContext(TradeContext);
  const authCtx = useContext(AuthContext);

  const headers = {
    "Acting-as": "dealer",
    Authorization: `JWT ${authCtx?.token}`,
  };

  const [exchangeRateValue, setExchangeRateValue] = useState(tradeCtx?.trade?.max_exchange_rate) || 0;
  const [bookedTradeLoading, setBookedTradeLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const pair = useMemo(() => {
    // if (t.trade_currency) return t.trade_currency.alpha_3;
    // if (t.currency_pair) return t.currency_pair
    if (t.fx_currency && t.trade_currency)
      return `${t.trade_currency.alpha_3}/${t.fx_currency.alpha_3}`
    return '—/—'
  }, [t])

  const clientRate = t.max_exchange_rate ?? t.exchange_rate ?? t.rate
  const tradeAmount = t.trade_amount ?? t.amount
  const fxAmount = t.fx_amount
  const marketRate = t.market_rate ?? t.bank_rate ?? null

  const bookedAt = t.trade_date|| null;
  const formatDate = useMemo(() => (value) => {
    try { return value ? new Date(value).toLocaleString() : '—' } catch { return String(value || '—') }
  }, [])

  // Derived values for preview section
  const fxCurrencyCode = useMemo(() => {
    if (t?.trade_currency?.alpha_3) return t.trade_currency.alpha_3
    if (typeof pair === 'string' && pair.includes('/')) return pair.split('/')[1]
    return ''
  }, [t, pair])

  const parsedEnteredRate = useMemo(() => {
    const val = Number(exchangeRateValue)
    // Only treat as valid if it's a finite number and not just an empty string or lone dot
    if (!Number.isFinite(val)) return null
    if (exchangeRateValue === '' || exchangeRateValue === '.') return null
    return val
  }, [exchangeRateValue])

  const convertedAmountAtEnteredRate = useMemo(() => {
    if (fxAmount == null || parsedEnteredRate == null) return null
    const base = Number(fxAmount)
    if (!Number.isFinite(base)) return null
    const result = base * parsedEnteredRate
    return Number.isFinite(result) ? result : null
  }, [fxAmount, parsedEnteredRate])

  const handleExchangeRateChange = (value) => {
    // Allow empty input, 0, just a decimal point, or valid numbers with up to 3 decimal places
    const isValid = (
      value === '' ||                          
      value === '.' ||                      
      /^\d+$/.test(value) ||                 
      /^\d*\.\d{0,4}$/.test(value) ||        
      /^\d+\.$/.test(value)                   
    );
  
    if (isValid) {
      setExchangeRateValue(value);
    }
  }

  const submitTradeBooked = async () => {
    try {
      setError(false);
      setErrorMessage('');
      if(exchangeRateValue === '' || exchangeRateValue === '.' || exchangeRateValue == null || isNaN(Number(exchangeRateValue)) || Number(exchangeRateValue) <= 0) {
        setError(true);
        setErrorMessage('Not a valid exchange rate');
        return;
      }
      setBookedTradeLoading(true);
      // const body = props.trade;
      // body.exchange_rate = exchangeRateValue;
      // body.dealer = authCtx?.correlation_key;
      const body = {
        exchange_rate: exchangeRateValue,
        dealer: authCtx?.correlation_key,
      }
      const tradeId = props.trade.id;
      const compelteTradeRes = await axios.post(`${process.env.REACT_APP_SERVER_URL}/trades/${tradeId}/set_booked/`, body, { headers: headers })

      if(compelteTradeRes.status === 200) {
        // tradeCtx.getTrades();
        // tradeCtx.trades = tradeCtx.trades.find((trade) => trade.id === props.tradeId).trade_status = 1;
        tradeCtx.setShowToast(true);
        tradeCtx.setToastResult('success');
        tradeCtx.setToastMessage('Trade(s) booked successfully');
        tradeCtx.getTrades();
        tradeCtx.updateTrade(props.trade?.id, { status: "COMPLETED" });
        tradeCtx.setTrade({ ...props.trade, status: "COMPLETED" });
        setBookedTradeLoading(false);
        props.close();
      }
    } catch (error) {
      console.log('Error completing trade', error);
      setError(true);
      setErrorMessage(error.response?.data?.message || 'Failed to complete trade');
      setBookedTradeLoading(false);
    }
  }

  return (
    <Container>
  <Title><FiEdit3 size={20} /> Manual Trade Instruction</Title>

      <Card>
        <Header>
          <Left>
            <PairBadge>{pair}</PairBadge>
            <Meta title={bookedAt}><FiClock /> {formatDate(bookedAt)}</Meta>
          </Left>
          <Right>
            <Meta>Amount</Meta>
            <Strong>{tradeAmount != null ? Number(tradeAmount).toLocaleString() : '—'}</Strong>
            <Meta><FiTrendingUp /> Client Rate</Meta>
            <Strong>{clientRate != null ? clientRate : '—'}</Strong>
          </Right>
        </Header>
        <Body>
          <Row>
            <Label>Currencies</Label>
            <Value>{pair}</Value>
          </Row>
          {marketRate != null && (
            <Row>
              <Label>Market Rate</Label>
              <Value>{marketRate}</Value>
            </Row>
          )}
          <Row>
            <Label>Max Client Rate</Label>
            <Value>{clientRate != null ? clientRate : '—'}</Value>
          </Row>
          <Row>
            <Label>Amount</Label>
            <Value>{tradeAmount != null ? Number(tradeAmount).toLocaleString() : '—'}</Value>
          </Row>
          {fxAmount != null && (
            <Row>
              <Label>FX Amount</Label>
              <Value>{Number(fxAmount).toLocaleString()}</Value>
            </Row>
          )}
          <Row>
            <Label>Booked at</Label>
            <Value>{formatDate(bookedAt)}</Value>
          </Row>


          {/* Booking details summary here */}

          <BookContainer>            
                {props.trade?.status !== 'BOOKED' && (
                  <div>
                  <div><label className="form-label">Confirm Exchange Rate</label></div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '250px' }}>
                    <input
                      type="text"
                      value={exchangeRateValue}
                      onChange={(event) => handleExchangeRateChange(event.target.value)}
                      placeholder="Enter exchange rate"
                      className="exchange_rate_input"
                      style={{ width: '100%' }}
                    />
                    <Button variant="success" onClick={submitTradeBooked} loading={bookedTradeLoading} style={{ width: '100%' }}>Confirm</Button>
                  </div>
                  {error && <div style={{ color: 'red', marginTop: '8px' }}>{errorMessage}</div>}
                </div>
                )}
          </BookContainer>
        </Body>
      </Card>


    </Container>
  )
}

TradeViewManualContainer.propTypes = {
  trade: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currencyPair: PropTypes.string,
    currency_pair: PropTypes.string,
    source_currency: PropTypes.string,
    destination_currency: PropTypes.string,
    max_exchange_rate: PropTypes.number,
    exchange_rate: PropTypes.number,
    rate: PropTypes.number,
    trade_amount: PropTypes.number,
    fx_amount: PropTypes.number,
    market_rate: PropTypes.number,
    bank_rate: PropTypes.number,
    booked_at: PropTypes.string,
    created_at: PropTypes.string,
    createdAt: PropTypes.string,
    timestamp: PropTypes.string,
    status: PropTypes.string,
  }),
  close: PropTypes.func.isRequired,
}

export default TradeViewManualContainer

